import * as React from 'react';
import { fetchApi } from '../../services/api';
import { formatNumber } from '@progress/kendo-intl';
import { RadialGauge } from '@progress/kendo-react-gauges';
import { Button } from '@progress/kendo-react-buttons';

enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export type DailyGoalRevenueResponse = Array<{ DayOfWeek: DayOfWeek, Goal: number, Actual: number, Today: boolean }>;

export const DayOfWeekColors = [
  '#FF0000', // Monday (Red)
  '#FFA500', // Tuesday (Orange)
  '#FFFF00', // Wednesday (Yellow)
  '#008000', // Thursday (Green)
  '#0000FF', // Friday (Blue)
  '#4B0082', // Saturday (Indigo)
  '#8A2BE2', // Sunday (Violet/BlueViolet)
];

type State = {
  loading: boolean;
  data: DailyGoalRevenueResponse;
  error?: string;
}

type Props = {
}

export default class DailyGoalRevenue extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: true,
      data: [],
    }

    this.refreshData = this.refreshData.bind(this);
  }

  public componentDidMount() {
    this.refreshData();
  }

  public refreshData() {
    fetchApi(`/api/Report/DailyGoals`)
      .then((response: DailyGoalRevenueResponse) => {
        this.setState({
          loading: false,
          data: response,
          error: undefined,
        });
      })
      .catch((error) => {
        this.setState({
          loading: false,
          error: error?.detail || 'Failed to load data'
        });
      });
  }

  public render() {
    if (this.state.loading) {
      return <div className="col-sm-6 col-md-4 col-lg-3 col-xl-2 report-card"></div>;
    }

    if (this.state.error) {
      return (
        <div className="col-sm-6 col-md-4 col-lg-3 col-xl-2 report-card text-center p-3">
          <p style={{ color: '#d32f2f', marginBottom: '10px' }}>{this.state.error}</p>
          <Button onClick={this.refreshData} fillMode='flat'>
            Retry
          </Button>
        </div>
      );
    }

    const dailyGoals = this.state.data;
    const goal = dailyGoals.find(g => g.Today);
    const pointer = dailyGoals.slice(0, dailyGoals.findIndex(g => g.Today) + 1).reduce((sum, goal) => sum + goal.Actual, 0);
    const weeklyTarget = dailyGoals.reduce((sum, goal) => sum + goal.Goal, 0);

    return <div className="col-sm-6 col-md-4 col-lg-3 col-xl-2 report-card report-card-gauge">
            <p>
              <span className="report-card-title">Daily Goal and Week Rev.</span>
              <br />
              {formatNumber(goal.Goal / 1000, 'c0')}k Goal / {formatNumber(goal.Actual / 1000, 'c0')}k Actual<br />
              {formatNumber(pointer, 'c0')} WTD / {formatNumber(weeklyTarget / 1000000, 'c1')}m Target
            </p>
            <RadialGauge
              transitions={false}
              pointer={{ value: pointer / 1000000 }}
              scale={{
                max: Math.max(weeklyTarget, pointer) / 1000000,
                labels: {
                  format: "{0:c1}m",
                },
                ranges: dailyGoals.reduce((ranges, goal, index) => {
                  const from = ranges.length > 0 ? ranges[ranges.length - 1].to : 0;
                  const to = from + goal.Goal / 1000000;
                  ranges.push({ from, to, color: DayOfWeekColors[index] });
                  return ranges;
                }, []),
              }}
            />
          </div>
  }
}
